import React, { useMemo, useState } from "react";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Form } from "react-bootstrap";
import { buildEbridgeQuoteRequest } from "../../../utils/ebridgeHelpers";
import { Loader } from "../../common/loader";

const validationSchema = Yup.object({
  customerName: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .required("Customer name is required"),
  nationalId: Yup.string().required("National ID / Civil ID is required"),
  mobileNo: Yup.string()
    .matches(/^\d{8}$/, "Mobile number must be 8 digits")
    .required("Mobile number is required"),
  email: Yup.string().email("Invalid email format"),
  address: Yup.string()
    .min(3, "Address must be at least 3 characters")
    .required("Address is required"),
});

const EbridgeMotorPersonalDetailForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const { data: planData, ropRequestData, vehicleData, valuationData } =
    location?.state || {};

  const initialValues = {
    customerName: "",
    nationalId: "",
    mobileNo: ropRequestData?.form_data?.mobile_no || "",
    email: "",
    address: "",
  };

  if (!planData) {
    return <Loader type="logo-gif" />;
  }

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setApiError(null);
    try {
      const baseRequest = buildEbridgeQuoteRequest(
        ropRequestData,
        vehicleData,
        valuationData
      );
      const policyRequest = {
        ...baseRequest,
        customerName: values.customerName,
        nationalId: values.nationalId,
        mobileNo: values.mobileNo,
        email: values.email || undefined,
        address: values.address,
      };

      // createEbridgePolicyService is not required for now
      const response = {};

      localStorage.setItem(
        "product_detail",
        JSON.stringify({
          productName:
            planData.sub_product_id === 21 ? "Comprehensive" : "Third Party",
          companyName: planData.company_name,
          price: planData.premium_value,
        })
      );

      navigate("/checkout", {
        state: {
          existingData: {
            ...response,
            is_ebridge: true,
            targetCode: planData.company_code,
            company_name: planData.company_name,
            company_id: planData.company_code,
            premium_value: planData.premium_value,
            premium_currency: "OMR",
            planData,
          },
        },
      });
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to create policy. Please try again.";
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitting) {
    return <Loader type="logo-gif" />;
  }

  return (
    <div className="container my-5">
      <div className="card shadow-sm">
        <div className="card-header bg-dark text-white">
          <h4 className="mb-0">
            {planData.company_name} — Personal Details
          </h4>
        </div>
        <div className="card-body">
          {apiError && (
            <div className="alert alert-danger" role="alert">
              {apiError}
            </div>
          )}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formSubmit }) => (
              <Form onSubmit={formSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="customerName" className="form-label">
                      Customer Name <span className="text-danger">*</span>
                    </label>
                    <Form.Control
                      id="customerName"
                      name="customerName"
                      type="text"
                      value={values.customerName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.customerName && errors.customerName
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="customerName"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="nationalId" className="form-label">
                      National ID / Civil ID <span className="text-danger">*</span>
                    </label>
                    <Form.Control
                      id="nationalId"
                      name="nationalId"
                      type="text"
                      value={values.nationalId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.nationalId && errors.nationalId
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="nationalId"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="mobileNo" className="form-label">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <Form.Control
                      id="mobileNo"
                      name="mobileNo"
                      type="text"
                      value={values.mobileNo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.mobileNo && errors.mobileNo ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="mobileNo"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="email" className="form-label">
                      Email
                    </label>
                    <Form.Control
                      id="email"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.email && errors.email ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="address" className="form-label">
                      Address <span className="text-danger">*</span>
                    </label>
                    <Form.Control
                      id="address"
                      name="address"
                      type="text"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`element-style ${
                        touched.address && errors.address ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="address"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                </div>

                <div className="form-submitbtn mt-2">
                  <Button type="submit" className="mt-2 h-btn">
                    Submit & Get Policy
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default EbridgeMotorPersonalDetailForm;
